import { useEffect, useId, useRef, useState } from 'react'
import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { qk } from '../api/queries'
import type { WorkspacesResponse } from '../api/types'
import { useSession } from '../context/SessionContext'
import { Icon, SymoneMark } from './Icon'
import { ALL_NAV, NAV_GROUPS } from './nav'
import { GettingStartedView } from '../views/GettingStartedView'
import { ApiKeysView } from '../views/ApiKeysView'
import { ToolkitsView } from '../views/ToolkitsView'
import { TriggersView } from '../views/TriggersView'
import { LogsView } from '../views/LogsView'
import { PlaygroundView } from '../views/PlaygroundView'
import { DirectoryView } from '../views/DirectoryView'
import { SessionsView } from '../views/SessionsView'
import { SettingsView } from '../views/SettingsView'
import { ComingSoonView } from '../views/ComingSoonView'
import { COMING_SOON } from '../views/comingSoonContent'
import './Shell.css'

const DOCS_URL: string | undefined = import.meta.env.VITE_DOCS_URL
const SUPPORT_URL: string | undefined = import.meta.env.VITE_SUPPORT_URL

function WorkspaceSwitcher() {
  const { workspaceId, switchWorkspace } = useSession()
  const [error, setError] = useState<string | null>(null)
  const { data } = useQuery({
    queryKey: qk.workspaces,
    queryFn: () => apiFetch<WorkspacesResponse>('/workspaces'),
  })
  const workspaces = data?.workspaces ?? []
  const id = useId()

  return (
    <div className="ws-switcher">
      <label htmlFor={id} className="sr-only">Workspace</label>
      <select
        id={id}
        className="ws-select"
        value={workspaceId ?? ''}
        onChange={(e) => {
          setError(null)
          switchWorkspace(e.target.value).catch(() => setError('Couldn’t switch workspace.'))
        }}
      >
        {workspaces.length === 0 && <option value={workspaceId ?? ''}>Current workspace</option>}
        {workspaces.map((w) => (
          <option key={w.id} value={w.id}>{w.name}</option>
        ))}
      </select>
      <Icon name="chevronDown" size={14} className="ws-select-chevron" />
      {error && <p className="ws-error" role="alert">{error}</p>}
    </div>
  )
}

function NavSearch() {
  const [q, setQ] = useState('')
  const navigate = useNavigate()
  const matches = q.trim()
    ? ALL_NAV.filter((n) => n.label.toLowerCase().includes(q.trim().toLowerCase()))
    : []

  return (
    <div className="nav-search">
      <Icon name="search" size={14} className="nav-search-icon" />
      <input
        type="search"
        className="nav-search-input"
        placeholder="Search"
        aria-label="Search pages"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && matches[0]) {
            navigate(matches[0].path)
            setQ('')
          }
          if (e.key === 'Escape') setQ('')
        }}
      />
      {matches.length > 0 && (
        <ul className="nav-search-results" role="listbox" aria-label="Matching pages">
          {matches.map((m) => (
            <li key={m.path} role="option" aria-selected={false}>
              <button type="button" onClick={() => { navigate(m.path); setQ('') }}>
                <Icon name={m.icon} size={14} /> {m.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function AccountMenu() {
  const { session, logout } = useSession()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { data } = useQuery({
    queryKey: qk.workspaces,
    queryFn: () => apiFetch<WorkspacesResponse>('/workspaces'),
  })
  const ws = data?.workspaces?.find((w) => w.id === session?.workspace.id)
  const user = session?.user
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'Account'

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="account" ref={ref}>
      <button
        type="button"
        className="account-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="account-avatar" aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span>
        <span className="account-text">
          <span className="account-name">{name}</span>
          <span className="account-ws">{ws?.name ?? session?.workspace.role ?? ''}</span>
        </span>
        <Icon name="chevronDown" size={14} />
      </button>
      {open && (
        <div className="account-menu" role="menu">
          <div className="account-menu-meta">
            <span>{user?.email}</span>
            <span className="muted">{session?.workspace.role} · {session?.workspace.plan} plan</span>
          </div>
          <button type="button" role="menuitem" className="account-menu-item" onClick={() => void logout()}>
            <Icon name="logout" size={14} /> Logout
          </button>
        </div>
      )}
    </div>
  )
}

function FooterLink({ href, icon, label }: { href?: string; icon: 'help' | 'book'; label: string }) {
  if (!href) {
    return (
      <span className="shell-nav-link shell-nav-link--muted" title={`${label} link not configured`}>
        <Icon name={icon} /> {label}
      </span>
    )
  }
  return (
    <a className="shell-nav-link" href={href} target="_blank" rel="noreferrer">
      <Icon name={icon} /> {label}
      <Icon name="external" size={12} className="shell-nav-ext" />
    </a>
  )
}

export function Shell() {
  return (
    <div className="shell">
      <nav className="shell-sidebar" aria-label="Main navigation">
        <div className="shell-brand">
          <SymoneMark />
          <span className="shell-brand-name">Symone</span>
          <span className="mode-chip">Developer</span>
        </div>

        <WorkspaceSwitcher />
        <NavSearch />

        {NAV_GROUPS.map((group, i) => (
          <div key={i} className="shell-nav-group">
            {i > 0 && <hr className="shell-divider" />}
            {group.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => 'shell-nav-link' + (isActive ? ' active' : '')}
              >
                <Icon name={item.icon} />
                <span>{item.label}</span>
                {item.soon && <span className="soon-dot" title="Coming soon" aria-label="coming soon" />}
              </NavLink>
            ))}
          </div>
        ))}

        <div className="shell-spacer" />

        <div className="shell-footer">
          <FooterLink href={SUPPORT_URL} icon="help" label="Support" />
          <FooterLink href={DOCS_URL} icon="book" label="Documentation" />
          <AccountMenu />
        </div>
      </nav>

      <main className="shell-main" id="main-content">
        <Routes>
          <Route index element={<Navigate to="/getting-started" replace />} />
          <Route path="/getting-started" element={<GettingStartedView />} />
          <Route path="/api-keys" element={<ApiKeysView />} />
          <Route path="/toolkits" element={<ToolkitsView />} />
          <Route path="/triggers" element={<TriggersView />} />
          <Route path="/logs" element={<LogsView />} />
          <Route path="/playground" element={<PlaygroundView />} />
          <Route path="/directory" element={<DirectoryView />} />
          <Route path="/sessions" element={<SessionsView />} />
          <Route path="/settings" element={<SettingsView />} />
          {Object.entries(COMING_SOON).map(([path, content]) => (
            <Route key={path} path={path} element={<ComingSoonView {...content} />} />
          ))}
          <Route path="*" element={<Navigate to="/getting-started" replace />} />
        </Routes>
      </main>
    </div>
  )
}
